import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchAdmin = async () => {
    try {
      const response = await api.get('/admin/me');
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/admin/login', { email, password });
    return response.data;
  };

  const verifyMFA = async (code, tempToken) => {
    const response = await api.post(`/admin/mfa/verify?temp_token=${tempToken}`, { code });
    const { access_token } = response.data;
    await setAccessToken(access_token);
    return response.data;
  };

  const register = async (email, password) => {
    const response = await api.post('/admin/register', { email, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setAdmin(null);
  };

  const setupMFA = async () => {
    const response = await api.post('/admin/mfa/setup', {});
    return response.data;
  };

  const enableMFA = async (code) => {
    const response = await api.post('/admin/mfa/enable', { code });
    await fetchAdmin();
    return response.data;
  };

  const disableMFA = async (code) => {
    const response = await api.post('/admin/mfa/disable', { code });
    await fetchAdmin();
    return response.data;
  };

  const setAccessToken = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);

    try {
      const response = await api.get('/admin/me', {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin after login:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      admin,
      token,
      loading,
      login,
      verifyMFA,
      register,
      logout,
      setupMFA,
      enableMFA,
      disableMFA,
      setAccessToken,
      isAuthenticated: !!admin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
